import { AxiosError } from "axios";
import { z, ZodError } from "zod";
import { PhoneProviderError } from "../../modules/call-center/call-center.errors";

const customErrorSchema = z.object({
  data: z.object({
    error: z.string(),
    details: z.string().array().optional(),
  }),
});

const customErrorStringDetailsSchema = z.object({
  data: z.object({
    error: z.string(),
    details: z.string().optional(),
  }),
});

export function formatErrorResponse(error: unknown) {
  if (error instanceof ZodError) {
    return error.errors
      .map((err) => {
        const path = err.path.join(".");
        const message = err.message;
        return `${path}: ${message}`;
      })
      .join(", ");
  }

  if (error instanceof AxiosError) {
    const err = customErrorSchema.safeParse(error.response);
    if (err.success) {
      return err.data.data.details?.join(", ") ?? err.data.data.error;
    }

    const errStringDetails = customErrorStringDetailsSchema.safeParse(error.response);
    if (errStringDetails.success) {
      return errStringDetails.data.data.details ?? errStringDetails.data.data.error;
    }
  }

  if (error instanceof PhoneProviderError) {
    return error.message;
  }

  return `${error}`;
}

/**
 * Whether a rejected request carries a particular server error code.
 *
 * Some refusals are worth acting on rather than only reporting: the server names those
 * with a stable code -- a `TypedConflictError` answers 409 with `{ error: "<CODE>" }` --
 * so the client can offer the fix instead of a dead end.
 *
 * Matching the code rather than the message is the whole point. The message is prose
 * and will be reworded; the code is the contract. Matching `error.message` would in any
 * case be matching the wrong string, since an AxiosError's message is "Request failed
 * with status code 409" and says nothing about what was refused.
 *
 * Lives here rather than beside its caller because this file already owns the shape of
 * an error response body, and `customErrorSchema` is exactly the parse it needs.
 */
export function isServerErrorCode(error: unknown, code: string, status = 409): boolean {
  if (!(error instanceof AxiosError) || error.response?.status !== status) {
    return false;
  }

  const parsed = customErrorSchema.safeParse(error.response);

  return parsed.success && parsed.data.data.error === code;
}
